import { AppsFlyer } from '../plugins/apps_flyer_plugin.js';
import { reportAppsflyerAttribution } from '../api/requests/attribution.requests.js';

const CONVERSION_DATA_ATTEMPTS = 5;
const CONVERSION_DATA_RETRY_MS = 2000;

const STRING_FIELDS: Record<string, string> = {
  af_status: 'attribution_status',
  media_source: 'media_source',
  campaign: 'campaign',
  campaign_id: 'campaign_id',
  af_channel: 'channel',
  agency: 'agency',
  af_adset: 'adset',
  af_adset_id: 'adset_id',
  af_ad: 'adgroup',
  af_ad_id: 'adgroup_id',
  af_siteid: 'af_siteid',
  af_sub1: 'af_sub1',
  af_sub2: 'af_sub2',
  af_sub3: 'af_sub3',
  af_sub4: 'af_sub4',
  af_sub5: 'af_sub5',
  install_time: 'install_time',
  click_time: 'click_time',
  http_referrer: 'http_referrer',
  retargeting_conversion_type: 'retargeting_conversion_type',
  af_click_lookback: 'af_click_lookback',
  af_reengagement_window: 'af_reengagement_window',
  shortlink: 'shortlink',
  deep_link_value: 'deep_link_value',
  af_dp: 'af_dp',
  af_cost_value: 'af_cost_value',
  cost_cents_usd: 'cost_cents_usd',
  orig_cost: 'orig_cost',
  af_cpi: 'af_cpi',
};

function toBool( value: unknown ): boolean | undefined
{
  if ( typeof value === 'boolean' ) return value;
  if ( typeof value === 'string' ) return value.toLowerCase() === 'true';
  return undefined;
}

function buildAttributionPayload( data: Record<string, unknown> ): Record<string, unknown>
{
  const media_source = data.media_source != null ? String( data.media_source ) : null;
  const af_status = data.af_status != null ? String( data.af_status ) : null;
  const user_source = media_source ?? af_status ?? 'organic';

  const payload: Record<string, unknown> = { user_source, raw_data: data };

  for ( const [ raw_key, column ] of Object.entries( STRING_FIELDS ) )
  {
    const value = data[ raw_key ];
    if ( value != null ) payload[ column ] = String( value );
  }

  const first_launch = toBool( data.is_first_launch );
  if ( first_launch !== undefined ) payload.is_first_launch = first_launch;

  const retargeting = toBool( data.is_retargeting );
  if ( retargeting !== undefined ) payload.is_retargeting = retargeting;

  const appsflyer_id = data.appsflyer_id ?? data.appsflyer_device_id;
  if ( appsflyer_id != null ) payload.appsflyer_id = String( appsflyer_id );

  return payload;
}

function delay( ms: number ): Promise<void>
{
  return new Promise( resolve => setTimeout( resolve, ms ) );
}

export async function syncAppsflyerAttribution(): Promise<void>
{
  if ( !AppsFlyer.isAvailable() ) return;

  let data: Record<string, unknown> | null = null;
  for ( let attempt = 0; attempt < CONVERSION_DATA_ATTEMPTS; attempt++ )
  {
    data = await AppsFlyer.getConversionData();
    if ( data && Object.keys( data ).length > 0 ) break;
    await delay( CONVERSION_DATA_RETRY_MS );
  }

  if ( !data || Object.keys( data ).length === 0 ) return;

  try
  {
    await reportAppsflyerAttribution( buildAttributionPayload( data ) );
  }
  catch ( e )
  {
    console.warn( '[AppsFlyer] attribution sync failed', e );
  }
}
